/**
 * The two decisions that are a THEME read rather than a token: a field's edge, and a link's ink.
 *
 * Both exist because a role token that is right in the light room is not automatically right in
 * the dark one, and neither can be fixed where it belongs — `global.css` is generated from
 * `LaunchPad/ideas/_design.md` by `scripts/gen-theme.mjs`, so a hand edit there survives until the
 * next build and then silently stops being the app. The generated values stay; the COMPONENT picks
 * which of them it wears, exactly as `useCardEdge` in `ui/card.tsx` already does.
 *
 * Store reads and not NativeWind's `dark:` variant, for the same reason `useCardEdge` is one: the
 * app's dark mode is `theme` in the store, applied through `colorScheme.set`, and every other
 * theme-dependent drawing decision in this codebase is made this way. One convention.
 */
import { useStore } from '@/store';

/**
 * The hairline round a field — an input, a textarea, the dashed frame of a step preview.
 *
 * `--input` and `--border` are the same value in all four theme blocks, and in the two dark rooms
 * that value sits at 1.75:1 (Bouquet) and 1.45:1 (Tuxedo) against `--card`: a border nobody can
 * see is a field nobody can see, which is what the readiness capture showed. `--line-strong` is
 * the stronger hairline the sheets already carry for exactly this — the one the Chip takes on
 * hover and the ⓘ takes always — and it is defined in every block, so this is a token swap and
 * not a new colour.
 */
export function useFieldEdge(): string {
  return useStore((s) => s.theme) === 'dark' ? 'border-line-strong' : 'border-input';
}

/**
 * The ink a pressable WORD is drawn in — a ghost button, a link.
 *
 * `--primary` is the app's red in all four blocks, which clears 5.99:1 on the light rooms' white
 * card and 2.76:1 / 2.56:1 on the dark ones. Under 4.5:1 a ghost stops reading as a control at
 * all: the readiness review saw "Change my name", "Switch" and the theme toggle as disabled text,
 * and they were the same red at a quarter of the contrast it has in daylight.
 *
 * `--gold-text` is the one accent token that clears in BOTH dark rooms (12.96:1 on Bouquet's
 * card, 6.12:1 on Tuxedo's) and it is already the app's emphasis-in-the-dark — the Saved line on
 * the preference sheet, the trace and points inks. Red by day, gilt by night.
 */
export function useLinkInk(): string {
  return useStore((s) => s.theme) === 'dark' ? 'text-gold-text' : 'text-primary';
}
